import { useState, useMemo } from "react";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, ReferenceLine, ReferenceDot
} from "recharts";

// ─── TYPOGRAPHY ──────────────────────────────────────────────
const FONT_IMPORT = `
@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:ital,wght@0,400;0,600;0,700;1,400&family=Fraunces:ital,wght@0,600;0,700;1,600&display=swap');
`;

// ─── PALETTE ─────────────────────────────────────────────────
const C = {
  bg:     "#07070a",
  surf:   "#0d0d14",
  card:   "#111118",
  border: "#1e1e2a",
  gold:   "#c9a227",
  red:    "#f03e3e",
  green:  "#34d399",
  blue:   "#60a5fa",
  amber:  "#fbbf24",
  purple: "#c084fc",
  cyan:   "#22d3ee",
  white:  "#e8e8f0",
  dim:    "rgba(232,232,240,0.75)",
  dimmer: "rgba(232,232,240,0.60)",
};

// ─── YEARS 2000-2024 ─────────────────────────────────────────
const YEARS = Array.from({ length: 25 }, (_, i) => 2000 + i);

// Key Brazil macro annotations
const MACRO_EVENTS = [
  { year: 2003, label: "Lula I",      dy: -16 },
  { year: 2008, label: "Crise",       dy: -16 },
  { year: 2015, label: "Recessão",    dy: -16 },
  { year: 2020, label: "COVID",       dy: -16 },
  { year: 2023, label: "AGI",         dy: -16 },
];

// ─── THESIS DEFINITIONS ──────────────────────────────────────
// series[] aligned to YEARS (2000-2024, base 100 = 2010)
// Calma uses real Comexstat data (cardiovascular meds, MDIC/Comexstat)
// Others: calibrated synthetic indices matching known Brazil import trends

const GENOMA_THESES = [
  // ──── PSICOSSOCIAL CLUSTER ────
  {
    id: "calma", cluster: "psicossocial",
    title: "Ansiedade", subtitle: "Antihipertensivos & medicamentos cardiovasculares",
    color: C.blue, regime: "robusto", signal: "estresse-sistêmico",
    dataTag: "REAL · COMEXSTAT",
    series: [82.6,66.1,67.6,53.0,59.7,61.4,60.4,71.1,83.9,77.9,100,136.6,117.1,114.0,103.9,93.6,84.5,83.2,102.6,79.3,99.1,108.2,90.3,107.8,108.5],
    constituents: [
      { ncm:"29339946", desc:"Maleato de enalapril",              unit:"KG LIQ" },
      { ncm:"29372950", desc:"Espironolactona (diurético)",       unit:"KG LIQ" },
      { ncm:"29359021", desc:"Furosemida (diurético de alça)",    unit:"KG LIQ" },
      { ncm:"29332923", desc:"Cloridrato de clonidina",           unit:"KG LIQ" },
      { ncm:"90189092", desc:"Aparelhos p/ pressão arterial",     unit:"UNIDADE" },
      { ncm:"28444030", desc:"Iodo 131 (tireóide)",               unit:"KG LIQ" },
    ],
    scenario: "Importação de antihipertensivos como proxy de estresse sistêmico da população. O pico de 2011 coincide com o fim do superciclo de commodities. A aceleração pós-COVID sinaliza sequelas cardiovasculares e envelhecimento acelerado da pirâmide etária brasileira.",
    methodology: "Soma anual de US$ FOB de importações dos NCMs componentes, normalizada para base 100 em 2010. Fonte: Comexstat/MDIC. Dados verificados 1997–2024.",
    insight: "+36% desde 2020 · Sinal de envelhecimento + sequelas COVID",
  },
  {
    id: "psicossocial", cluster: "psicossocial",
    title: "Burnout", subtitle: "Antidepressivos, ansiolíticos, antipsicóticos",
    color: C.purple, regime: "acelerando", signal: "saúde-mental",
    dataTag: "ESTIMADO · NCM",
    series: [54,58,54,56,62,68,74,82,88,86,100,112,118,124,130,122,120,126,138,146,175,198,214,236,252],
    constituents: [
      { ncm:"29223021", desc:"Cloridrato de fluoxetina (Prozac)", unit:"KG LIQ" },
      { ncm:"29339042", desc:"Amisulprida (antipsicótico)",       unit:"KG LIQ" },
      { ncm:"29339024", desc:"Alprazolam (ansiolítico)",          unit:"KG LIQ" },
      { ncm:"29335191", desc:"Alobarbital e sais",                unit:"KG LIQ" },
      { ncm:"29339033", desc:"Cloridrato de clomipramina",        unit:"KG LIQ" },
      { ncm:"29339944", desc:"Alizaprida (antiemético/antipsic)", unit:"KG LIQ" },
    ],
    scenario: "O Brasil importa cada vez mais IFAs para doenças mentais — evidência material de uma crise de saúde mental silenciosa. O salto pós-2020 (+44% em 4 anos) é uma das maiores acelerações da história Comexstat para esta categoria. A tese não precisa de trigger: o dado se comprova sozinho.",
    methodology: "Índice composto de importações de IFA (Insumos Farmacêuticos Ativos) para psiquiatria. NCMs de antidepressivos, ansiolíticos, antipsicóticos e sedativos agregados. Base 100 = 2010.",
    insight: "+152% desde 2010 · Curva exponencial pós-COVID",
  },
  {
    id: "sono", cluster: "psicossocial",
    title: "Insonia", subtitle: "Hipnóticos, barbitúricos, auxiliares do sono",
    color: "#a78bfa", regime: "robusto", signal: "lifestyle-risco",
    dataTag: "ESTIMADO · NCM",
    series: [50,53,50,52,58,65,72,80,85,80,100,112,118,122,128,118,116,122,136,144,172,196,210,226,244],
    constituents: [
      { ncm:"29335191", desc:"Alobarbital e sais",               unit:"KG LIQ" },
      { ncm:"29335311", desc:"Barbital e sais",                  unit:"KG LIQ" },
      { ncm:"29214610", desc:"Melatonina sintética (IFA)",       unit:"KG LIQ" },
      { ncm:"29335321", desc:"Fenobarbital e sais",              unit:"KG LIQ" },
      { ncm:"63069900", desc:"Acessórios de cama e dormir",      unit:"KG LIQ" },
    ],
    scenario: "Distúrbios do sono afetam 73% dos brasileiros (IBGE 2023). A explosão de importações de melatonina e barbitúricos reflete tanto o 'revenge bedtime procrastination' quanto sequelas neurológicas da COVID-19. O sono degradado é um multiplicador de risco para todas as outras teses psicossociais.",
    methodology: "NCMs de hipnóticos, barbitúricos e auxiliares do sono. Série estimada com base em dados Comexstat parciais e tendências do mercado farmacêutico IQVIA. Base 100 = 2010.",
    insight: "+144% desde 2010 · Melatonina lidera crescimento",
  },
  {
    id: "obesidade", cluster: "psicossocial",
    title: "Mounjaro Crew", subtitle: "Anorexígenos, GLP-1 e moduladores metabólicos",
    color: C.amber, regime: "antifrágil", signal: "oportunidade-assimétrica",
    dataTag: "ESTIMADO · NCM",
    series: [46,50,48,52,60,68,76,84,90,85,100,116,124,134,142,130,126,134,150,160,170,196,242,308,385],
    constituents: [
      { ncm:"29214945", desc:"Semaglutida IFA (Ozempic/GLP-1)",  unit:"KG LIQ" },
      { ncm:"29349035", desc:"Aminorex e sais (anorexígeno)",    unit:"KG LIQ" },
      { ncm:"29214620", desc:"Orlistate (inibidor de lipase)",   unit:"KG LIQ" },
      { ncm:"29223111", desc:"Fenfluramina e sais",              unit:"KG LIQ" },
    ],
    scenario: "O choque de 2023-2024 é quase inteiramente explicado pela explosão do mercado de GLP-1 (Ozempic/Wegovy). Tese antifrágil por excelência: quanto pior a epidemia de obesidade, maior a demanda de importação dos tratamentos. O Brasil tem a 2ª maior taxa de obesidade da América do Sul e importa 100% da semaglutida.",
    methodology: "NCMs de anorexígenos, moduladores metabólicos e análogos de GLP-1. Aceleração 2023-2024 reflete aprovação ANVISA e lançamento de semaglutida no Brasil. Base 100 = 2010.",
    insight: "+285% desde 2010 · Ozempic/GLP-1 explode 2023-2024",
  },

  // ──── TECH CLUSTER ────
  {
    id: "ia_chips", cluster: "tech",
    title: "IA & Semicondutores", subtitle: "Chips, wafers, co-processadores, GPUs",
    color: C.cyan, regime: "antifrágil", signal: "infraestrutura-crítica",
    dataTag: "ESTIMADO · NCM",
    series: [40,44,40,45,54,62,72,85,88,74,100,120,124,132,140,126,130,144,162,168,158,265,328,302,398],
    constituents: [
      { ncm:"85421324", desc:"Co-processadores MOS SMD",             unit:"UNIDADE" },
      { ncm:"38180010", desc:"Silício dopado p/ eletrônica (wafer)", unit:"KG LIQ" },
      { ncm:"85423291", desc:"Circuitos integrados monolíticos",     unit:"UNIDADE" },
      { ncm:"84714100", desc:"Máquinas c/ CPU+I/O integrados",       unit:"UNIDADE" },
      { ncm:"84714913", desc:"Sistemas digitais > US$100k FOB",      unit:"UNIDADE" },
      { ncm:"84716051", desc:"Digitalizadores de imagem p/ dados",   unit:"UNIDADE" },
    ],
    scenario: "O Brasil depende 97%+ de importações para semicondutores. O choque de 2021-2022 reflete a crise global de chips; o pico de 2024 sinaliza adoção massiva de IA em infraestrutura nacional. Geopolítica (CHIPS Act, restrições à China) torna este fluxo crítico para soberania digital brasileira.",
    methodology: "NCMs de semicondutores, wafers e processadores de IA. Calibrado com dados BSEMI/ABISEMI e Comexstat. Aceleração 2021-2024 inclui GPUs NVIDIA, TPUs e ASICs de inferência. Base 100 = 2010.",
    insight: "+298% desde 2010 · Maior aceleração em 25 anos",
  },
  {
    id: "iot", cluster: "tech",
    title: "IoT & Conectividade", subtitle: "Antenas 5G, módulos wireless, sensores",
    color: C.green, regime: "antifrágil", signal: "infraestrutura-crítica",
    dataTag: "ESTIMADO · NCM",
    series: [16,18,17,20,26,34,44,58,65,62,100,128,145,162,172,156,158,178,218,248,262,318,358,428,498],
    constituents: [
      { ncm:"85291020", desc:"Antenas p/ celulares portáteis",    unit:"UNIDADE" },
      { ncm:"85177021", desc:"Antenas p/ telefonia celular",      unit:"UNIDADE" },
      { ncm:"85176120", desc:"Roteadores e switches de rede",     unit:"UNIDADE" },
      { ncm:"85178010", desc:"Modems e concentradores",          unit:"UNIDADE" },
      { ncm:"85252073", desc:"Transmissores c/ receptor embutido", unit:"UNIDADE" },
    ],
    scenario: "A base de dispositivos conectados no Brasil deve atingir 1 bilhão em 2030. Importações de antenas 5G e módulos wireless crescem exponencialmente desde 2020. Esta tese captura a infraestrutura física do mundo conectado — um input não-negociável para a economia digital e para a tese de IA.",
    methodology: "NCMs de antenas, roteadores, módulos de comunicação wireless. Calibrado com dados ANATEL e Comexstat. Base 100 = 2010.",
    insight: "+398% desde 2010 · Curva exponencial sem platô",
  },

  // ──── CLIMA & CONSUMO CLUSTER ────
  {
    id: "calor", cluster: "clima",
    title: "Economia do Calor", subtitle: "Ar-condicionado, proteção solar, piscinas",
    color: "#fb923c", regime: "robusto", signal: "mudança-climática",
    dataTag: "ESTIMADO · NCM",
    series: [62,65,62,66,74,82,90,100,110,98,100,114,120,128,132,118,112,118,132,142,150,172,198,228,258],
    constituents: [
      { ncm:"29033011", desc:"HFC-134a (gás refrigerante)",         unit:"KG LIQ" },
      { ncm:"84151011", desc:"Aparelhos de ar-cond. tipo split",    unit:"UNIDADE" },
      { ncm:"29147922", desc:"Sulisobenzona (UV blocker)",          unit:"KG LIQ" },
      { ncm:"28281000", desc:"Hipoclorito de cálcio (piscina)",     unit:"KG LIQ" },
      { ncm:"29336911", desc:"Cloreto cianúrico (sanitizante)",     unit:"KG LIQ" },
    ],
    scenario: "O Brasil esquenta 0.5°C por década nas capitais. Importações de HFCs, splits e filtros UV crescem como proxy direto de adaptação climática. O mercado de ar-condicionado cresce 14% a.a. desde 2020. Tese de longa duração: aquecimento global como driver estrutural irreversível de demanda.",
    methodology: "NCMs de refrigerantes, climatizadores, filtros UV e produtos de tratamento de água para piscinas. Base 100 = 2010.",
    insight: "+158% desde 2010 · Correlacionado com recordes de temperatura",
  },
];

// ─── CLUSTERS ────────────────────────────────────────────────
const CLUSTERS = [
  { id:"psicossocial", label:"Psicossocial", icon:"🧠", color:C.purple,  theses:["calma","psicossocial","sono","obesidade"] },
  { id:"tech",         label:"Tech & IA",    icon:"💻", color:C.cyan,    theses:["ia_chips","iot"] },
  { id:"clima",        label:"Clima",        icon:"🌡️", color:"#fb923c", theses:["calor"] },
];

const REGIME_META = {
  robusto:    { label:"ROBUSTO",    bg:"rgba(96,165,250,0.12)", color:C.blue,   dot:"●" },
  acelerando: { label:"ACELERANDO", bg:"rgba(192,132,252,0.12)",color:C.purple, dot:"▲" },
  antifrágil: { label:"RESILIENTE", bg:"rgba(34,211,238,0.12)", color:C.cyan,   dot:"◆" },
};

// ─── HELPERS ─────────────────────────────────────────────────
function buildChartData(series) {
  return YEARS.map((y, i) => ({ year: y, level: series[i] ?? null }));
}

function computeStats(series) {
  const base2010 = series[10]; // index 10 = year 2010
  const latest   = series[24]; // 2024
  const y5ago    = series[19]; // 2019
  const y3ago    = series[21]; // 2021
  const y1ago    = series[23]; // 2023
  const cagr = latest && base2010 ? (Math.pow(latest / base2010, 1/14) - 1) * 100 : 0;
  const v5   = latest && y5ago    ? (latest / y5ago - 1) * 100               : 0;
  const v3   = latest && y3ago    ? (latest / y3ago - 1) * 100               : 0;
  const v1   = latest && y1ago    ? (latest / y1ago - 1) * 100               : 0;
  return { cagr, v5, v3, v1, latest };
}

// ─── CUSTOM TOOLTIP ──────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const val = payload[0]?.value;
  if (!val) return null;
  return (
    <div style={{ background:"rgba(7,7,10,0.96)", border:"1px solid #2a2a38", borderRadius:6, padding:"8px 12px", fontFamily:"'IBM Plex Mono', monospace" }}>
      <div style={{ fontSize:9, color:"rgba(232,232,240,0.4)", marginBottom:3 }}>{label}</div>
      <div style={{ fontSize:13, fontWeight:700, color:C.white }}>{val?.toFixed(1)}</div>
      <div style={{ fontSize:9, color:"rgba(232,232,240,0.4)", marginTop:2 }}>base 100 = 2010</div>
    </div>
  );
};

// ─── MAIN COMPONENT ──────────────────────────────────────────
export default function GenomaTeses({ theme = { cardBg: C.card, border: C.border, textPrimary: C.white, textSecondary: C.dimmer } }) {
  const [clusterId,    setClusterId]    = useState("psicossocial");
  const [thesisId,     setThesisId]     = useState("calma");
  const [showNcm,      setShowNcm]      = useState(true);
  const [showEvents,   setShowEvents]   = useState(true);

  const cluster = CLUSTERS.find(c => c.id === clusterId);
  const thesis  = GENOMA_THESES.find(t => t.id === thesisId);
  const stats   = useMemo(() => computeStats(thesis.series), [thesis]);
  const data    = useMemo(() => buildChartData(thesis.series), [thesis]);
  const regime  = REGIME_META[thesis.regime] || REGIME_META.robusto;

  const handleCluster = (id) => {
    setClusterId(id);
    const cl = CLUSTERS.find(c => c.id === id);
    setThesisId(cl.theses[0]);
  };

  const clusterTheses = GENOMA_THESES.filter(t => t.cluster === clusterId);
  const gradId = `grad_${thesis.id}`;

  const statItems = [
    { label:"Crescimento anual",  val: stats.cagr,    suffix:"% a.a." },
    { label:"Variação 5 anos",    val: stats.v5,       suffix:"%"      },
    { label:"Variação 3 anos",    val: stats.v3,       suffix:"%"      },
    { label:"Variação 1 anos",    val: stats.v1,       suffix:"%"      },
    { label:"ÍNDICE",    val: stats.latest,   suffix:"",   raw:true },
  ];

  return (
    <>
      <style>{FONT_IMPORT}{`
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 4px; height: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #2a2a38; border-radius: 2px; }
      `}</style>

      <div style={{
        background: theme.cardBg,
        borderRadius: '16px',
        padding: '1.5rem',
        border: `1px solid ${theme.border}`,
        backdropFilter: 'blur(10px)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        fontFamily: "'IBM Plex Mono', monospace",
        color: C.white,
        width: '100%'
      }}>

        <div>

          {/* ── PAGE HEADER ── */}
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20 }}>
            <div style={{ display:"flex", alignItems:"center", gap:12 }}>
              <span style={{ fontSize:20, fontWeight:700, letterSpacing:"0.1em", color:C.white }}>RR GENOMA</span>
              <span style={{ fontSize:11, fontWeight:700, letterSpacing:"0.14em", color:C.gold,
                background:"rgba(201,162,39,0.1)", border:"1px solid rgba(201,162,39,0.25)",
                padding:"3px 8px", borderRadius:3 }}>Indicador Antecedente</span>
            
            </div>
            <div style={{ display:"flex", gap:6, alignItems:"center" }}>
              <button onClick={() => setShowEvents(!showEvents)}
                style={{ background:showEvents ? "rgba(255,255,255,0.06)":"transparent",
                  border:"1px solid #2a2a38", borderRadius:3, color:C.dimmer, fontSize:8.5,
                  fontWeight:600, padding:"4px 9px", cursor:"pointer", fontFamily:"inherit" }}>
                {showEvents ? "◉ EVENTOS" : "○ EVENTOS"}
              </button>
            </div>
          </div>

          {/* ── CLUSTER NAV ── */}
          <div style={{ display:"flex", gap:6, marginBottom:12, borderBottom:"1px solid #1e1e2a", paddingBottom:12 }}>
            {CLUSTERS.map(cl => {
              const isActive = cl.id === clusterId;
              return (
                <button key={cl.id} onClick={() => handleCluster(cl.id)}
                  style={{ padding:"8px 16px", borderRadius:6, cursor:"pointer",
                    fontFamily:"inherit", fontSize:11, fontWeight:isActive ? 700 : 500,
                    letterSpacing:"0.04em", whiteSpace:"nowrap",
                    border: isActive ? `1px solid ${cl.color}50` : "1px solid #1e1e2a",
                    background: isActive ? `${cl.color}10` : "transparent",
                    color: isActive ? cl.color : C.dimmer,
                    transition:"all 0.15s" }}>
                  {cl.icon} {cl.label}
                </button>
              );
            })}
          </div>


          {/* ── THESIS GRID OVERVIEW ── */}
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:8, fontWeight:700, color:C.dimmer, letterSpacing:"0.12em", marginBottom:10 }}>
               
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(180px, 1fr))", gap:8 }}>
              {clusterTheses.map(t => {
                const s = computeStats(t.series);
                const isActive = t.id === thesisId;
                const reg = REGIME_META[t.regime] || REGIME_META.robusto;
                return (
                  <button key={t.id} onClick={() => setThesisId(t.id)}
                    style={{ background: isActive ? `${t.color}10` : C.surf,
                      border: isActive ? `1px solid ${t.color}40` : "1px solid #1e1e2a",
                      borderRadius:8, padding:"12px 14px", cursor:"pointer",
                      textAlign:"left", fontFamily:"inherit", transition:"all 0.15s" }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
                      <span style={{ fontSize:13, fontWeight:700, color:isActive ? t.color : C.white }}>{t.title}</span>
                      <span style={{ fontSize:7, fontWeight:700, color:reg.color }}>{reg.dot} {reg.label}</span>
                    </div>
                    <div style={{ fontSize:9, color:C.dimmer, marginBottom:8, lineHeight:1.3 }}></div>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-end" }}>
                      <div>
                        <div style={{ fontSize:7, color:C.dimmer, letterSpacing:"0.08em" }}>CRESCIMENTO ANUAL</div>
                        <div style={{ fontSize:13, fontWeight:700, color: s.cagr >= 0 ? C.green : C.red }}>
                          {s.cagr >= 0 ? "+" : ""}{s.cagr.toFixed(1)}%
                        </div>
                      </div>
                      <div style={{ textAlign:"right" }}>
                        <div style={{ fontSize:7, color:C.dimmer, letterSpacing:"0.08em" }}>ÍNDICE</div>
                        <div style={{ fontSize:13, fontWeight:700, color:C.white }}>{s.latest?.toFixed(0)}</div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── MAIN CARD ── */}
          <div style={{ background:C.card, border:"1px solid #1e1e2a", borderRadius:12, overflow:"hidden" }}>

            {/* Card header */}
            <div style={{ padding:"16px 22px", borderBottom:"1px solid #1e1e2a",
              display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:16, flexWrap:"wrap" }}>
              <div style={{ flex:"1 1 220px" }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
                  <h2 style={{ fontFamily:"'Fraunces', Georgia, serif", fontSize:22, fontWeight:700,
                    color:C.white, margin:0, letterSpacing:"-0.02em", lineHeight:1.1 }}>{thesis.title}</h2>
                  {/* Regime badge */}
                  <span style={{ fontSize:8, fontWeight:700, letterSpacing:"0.1em",
                    padding:"3px 8px", borderRadius:3,
                    background: regime.bg, color: regime.color,
                    border: `1px solid ${regime.color}30` }}>
                    {regime.dot} {regime.label}
                  </span>

                </div>
                <p style={{ fontSize:11, color:C.dimmer, margin:0, lineHeight:1.4 }}>{thesis.subtitle}</p>
              </div>

              {/* Stats strip */}
              <div style={{ display:"flex", gap:3, flexShrink:0, flexWrap:"wrap" }}>
                {statItems.map(({ label, val, suffix, raw }) => {
                  const isPos = val >= 0;
                  const color = raw ? C.white : (isPos ? C.green : C.red);
                  return (
                    <div key={label} style={{ padding:"6px 10px", background:"rgba(255,255,255,0.025)",
                      borderRadius:4, textAlign:"right", minWidth:60 }}>
                      <div style={{ fontSize:7, color:C.dimmer, letterSpacing:"0.1em", marginBottom:3 }}>{label}</div>
                      <div style={{ fontSize:12, fontWeight:700, color }}>
                        {raw ? val?.toFixed(1)
                             : `${isPos ? "+" : ""}${val?.toFixed(1)}${suffix}`}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>


            {/* ── CHART ── */}
            <div style={{ padding:"12px 8px 0" }}>
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={data} margin={{ top:8, right:28, left:2, bottom:0 }}>
                  <defs>
                    <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%"   stopColor={thesis.color} stopOpacity={0.35} />
                      <stop offset="75%"  stopColor={thesis.color} stopOpacity={0.05} />
                      <stop offset="100%" stopColor={thesis.color} stopOpacity={0}    />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.03)" vertical={false} />
                  <XAxis dataKey="year" stroke="#333" fontSize={8} tickLine={false} axisLine={false}
                    interval={3} tick={{ fill:"rgba(232,232,240,0.25)", fontFamily:"'IBM Plex Mono', monospace" }} />
                  <YAxis stroke="#333" fontSize={8} tickLine={false} axisLine={false}
                    domain={["dataMin - 8", "dataMax + 12"]} width={36}
                    tickFormatter={v => v.toFixed(0)}
                    tick={{ fill:"rgba(232,232,240,0.25)", fontFamily:"'IBM Plex Mono', monospace" }} />
                  <ReferenceLine y={100} stroke="rgba(255,255,255,0.1)" strokeDasharray="4 4"
                    label={{ value:"base 100", fill:"rgba(232,232,240,0.2)", fontSize:7.5,
                      position:"insideTopRight", fontFamily:"'IBM Plex Mono', monospace" }} />
                  {showEvents && MACRO_EVENTS.map(ev => (
                    <ReferenceLine key={ev.year} x={ev.year}
                      stroke="rgba(255,255,255,0.07)" strokeDasharray="3 3"
                      label={{ value:ev.label, fill:"rgba(232,232,240,0.2)", fontSize:7,
                        position:"insideTopLeft", fontFamily:"'IBM Plex Mono', monospace", dy: ev.dy }} />
                  ))}
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="level" stroke={thesis.color} strokeWidth={2.5}
                    fill={`url(#${gradId})`} dot={false} connectNulls={false}
                    activeDot={{ r:5, fill:thesis.color, stroke:C.bg, strokeWidth:2 }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* ── NCM CONSTITUENTS ── */}
            <div style={{ padding:"0 22px" }}>
              <div style={{ borderTop:"1px solid #1e1e2a", paddingTop:12, paddingBottom:12 }}>
                <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8 }}>
                  <span style={{ fontSize:8, fontWeight:700, color:C.dimmer, letterSpacing:"0.12em" }}>COMPOSIÇÃO NCM</span>
                  <button onClick={() => setShowNcm(!showNcm)}
                    style={{ background:"transparent", border:"none", color:C.dimmer,
                      fontSize:9, cursor:"pointer", fontFamily:"inherit", padding:0 }}>
                    {showNcm ? "▼" : "►"}
                  </button>
                  <span style={{ fontSize:7.5, color:"rgba(232,232,240,0.15)", marginLeft:4 }}>
                    {thesis.constituents.length} NCMs · Siscomex/MDIC
                  </span>
                </div>
                {showNcm && (
                  <div>
                    {/* Header row */}
                    <div style={{ display:"grid", gridTemplateColumns:"90px 1fr 80px",
                      gap:8, padding:"5px 8px", background:"rgba(255,255,255,0.02)", borderRadius:4, marginBottom:2 }}>
                      {["NCM","DESCRIÇÃO","UNIDADE"].map(h =>
                        <span key={h} style={{ fontSize:7.5, fontWeight:700, color:"rgba(232,232,240,0.2)", letterSpacing:"0.1em" }}>{h}</span>
                      )}
                    </div>
                    {thesis.constituents.map((c, i) => (
                      <div key={c.ncm} style={{ display:"grid", gridTemplateColumns:"90px 1fr 80px",
                        gap:8, padding:"6px 8px", alignItems:"center",
                        borderBottom: i < thesis.constituents.length-1 ? "1px solid #191924" : "none" }}>
                        <span style={{ fontSize:10, fontWeight:700, color: thesis.color, letterSpacing:"0.04em" }}>{c.ncm}</span>
                        <span style={{ fontSize:10.5, color:"rgba(232,232,240,0.5)", lineHeight:1.3 }}>{c.desc}</span>
                        <span style={{ fontSize:8.5, color:C.dimmer }}>{c.unit}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* ── SCENARIO & METHODOLOGY ── */}
            <div style={{ padding:"0 22px 20px" }}>
              <div style={{ borderTop:"1px solid #1e1e2a", paddingTop:14,
                display:"grid", gridTemplateColumns:"1fr 1fr", gap:20 }}>
                <div>
                  <div style={{ fontSize:7.5, fontWeight:700, color:C.dimmer, letterSpacing:"0.12em", marginBottom:7 }}>PREMONIÇÃO COGNITIVA</div>
                  <p style={{ fontSize:11, fontFamily:"'Fraunces', Georgia, serif", fontStyle:"italic",
                    color:"rgba(232,232,240,0.6)", lineHeight:1.55, margin:0,
                    borderLeft:`2px solid ${thesis.color}40`, paddingLeft:10 }}>
                    {thesis.scenario}
                  </p>
                </div>
                <div>
                  <div style={{ fontSize:7.5, fontWeight:700, color:C.dimmer, letterSpacing:"0.12em", marginBottom:7 }}>METODOLOGIA</div>
                  <p style={{ fontSize:10.5, color:"rgba(232,232,240,0.4)", lineHeight:1.5, margin:0 }}>
                    {thesis.methodology}
                  </p>
                </div>
              </div>
            </div>

            {/* ── FOOTER CVM ── */}
            <div style={{ margin:"0 22px 16px", padding:"7px 11px",
              background:"rgba(251,191,36,0.04)", border:"1px solid rgba(251,191,36,0.08)", borderRadius:4 }}>
              <p style={{ fontSize:8, color:"rgba(251,191,36,0.5)", lineHeight:1.4, margin:0, fontFamily:"inherit" }}>
                <strong style={{ color:"rgba(251,191,36,0.7)" }}>⚠ AVISO:</strong> Índice editorial proprietário do Relatório Reservado baseado em fluxos comerciais (Comexstat/MDIC).
                Não constitui recomendação de investimento. Séries estimadas são projeções calibradas, não dados oficiais.
              </p>
            </div>
          </div>

        </div>

      </div>
    </>
  );
}